<template>
  <div class="row">
    <div class="col-12 d-flex justify-content-between align-items-center mb-3">
      <h2>{{ $t('sepa_direct_debits.headline') }}</h2>
      <router-link class="btn btn-outline-secondary" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS, query: {tab: 'exports'}}">
        {{ $t('sepa_direct_debits.back_to_exports') }}
      </router-link>
    </div>

    <div class="col-12">
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <router-link class="nav-link" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS, query: {tab: 'settled'}}">
            {{ $t('sepa_direct_debits.settled_bookings') }}
          </router-link>
        </li>
        <li class="nav-item">
          <router-link class="nav-link" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS, query: {tab: 'submitted'}}">
            {{ $t('sepa_direct_debits.submitted_bookings') }}
          </router-link>
        </li>
        <li class="nav-item">
          <router-link class="nav-link" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS}">
            {{ $t('sepa_direct_debits.open_bookings') }}
          </router-link>
        </li>
        <li class="nav-item">
          <router-link class="nav-link" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS, query: {tab: 'exports'}}">
            {{ $t('sepa_direct_debits.exports') }}
          </router-link>
        </li>
        <li class="nav-item">
          <button class="nav-link active" type="button">
            {{ $t('sepa_direct_debits.export_tab', {id: exportId}) }}
          </button>
        </li>
      </ul>
    </div>

    <div class="col-12" v-if="currentExport">
      <div class="text-muted mb-3">
        {{ currentExport.message_id }} | {{ formatDate(currentExport.created_at, 'short2') }}
      </div>

      <div class="d-flex flex-wrap gap-3 align-items-center mb-3">
        <div>
          <div class="text-muted small">{{ $t('sepa_direct_debits.state') }}</div>
          <div>{{ currentExport.state }}</div>
        </div>
        <div>
          <div class="text-muted small">{{ $t('sepa_direct_debits.booking_count') }}</div>
          <div>{{ currentExport.booking_count }}</div>
        </div>
        <div>
          <div class="text-muted small">{{ $t('sepa_direct_debits.control_sum') }}</div>
          <div>{{ formatCurrency(currentExport.control_sum) }}</div>
        </div>
        <div>
          <div class="text-muted small">{{ $t('sepa_direct_debits.creditor_account') }}</div>
          <div>{{ currentExport.creditor_account_id }}</div>
        </div>
        <div class="ms-auto d-flex flex-wrap gap-2">
          <button v-if="currentExport.xml_url" class="btn btn-outline-primary" type="button" @click="downloadXml">
            {{ $t('sepa_direct_debits.download_xml') }}
          </button>
          <button v-if="currentExport.state === 'generated'" class="btn btn-outline-success" type="button" @click="submitExport">
            {{ $t('sepa_direct_debits.mark_submitted') }}
          </button>
          <button v-if="currentExport.state === 'generated'" class="btn btn-outline-danger" type="button" @click="discardExport">
            {{ $t('sepa_direct_debits.discard') }}
          </button>
        </div>
      </div>

      <DataTable stripedRows
                 dataKey="id"
                 :value="currentExport.payment_bookings || []"
                 :loading="sepaDirectDebitExportStore.loading"
                 responsiveLayout="scroll">
        <TableColumn field="requested_on" :header="$t('sepa_direct_debits.requested_on')">
          <template #body="{data}">{{ formatDate(data.requested_on, 'short2') }}</template>
        </TableColumn>
        <TableColumn field="client.kdnr" :header="$t('sepa_direct_debits.customer_number')">
          <template #body="{data}">{{ data.client?.kdnr || '-' }}</template>
        </TableColumn>
        <TableColumn field="client.name" :header="$t('sepa_direct_debits.customer')">
          <template #body="{data}">{{ data.client?.name || '-' }}</template>
        </TableColumn>
        <TableColumn field="invoice_number" :header="$t('sepa_direct_debits.invoice')">
          <template #body="{data}">
            <router-link v-if="data.invoice_id" :to="{name: ROUTES.DUNNING.EDIT, params: {invoice_id: data.invoice_id}}">
              {{ data.invoice_number || data.invoice_id }}
            </router-link>
            <span v-else>-</span>
          </template>
        </TableColumn>
        <TableColumn field="amount" :header="$t('sepa_direct_debits.amount')" headerClass="text-end" bodyClass="text-end">
          <template #body="{data}">
            <div class="text-end">{{ formatCurrency(data.amount) }}</div>
          </template>
        </TableColumn>
        <TableColumn field="sequence_type" :header="$t('sepa_direct_debits.sequence_type')"></TableColumn>
        <TableColumn field="remittance_information" :header="$t('sepa_direct_debits.remittance_information')"></TableColumn>
      </DataTable>
    </div>
  </div>
</template>

<script>
import {useSepaDirectDebitExportStore} from "@/stores/sepa_direct_debit_exports";
import {Helpers} from "@/helpers";
import {ROUTES} from "@/scripts/constants";

export default {
  name: 'SepaDirectDebitExportShow',
  setup() {
    return {
      sepaDirectDebitExportStore: useSepaDirectDebitExportStore(),
    }
  },
  computed: {
    ROUTES() {
      return ROUTES
    },
    exportId() {
      return this.$route.params.id
    },
    currentExport() {
      return this.sepaDirectDebitExportStore.currentExport
    },
  },
  created() {
    this.setTitle(this.$t('sepa_direct_debits.export_headline', {id: this.exportId}))
    this.loadExport()
  },
  methods: {
    loadExport() {
      return this.sepaDirectDebitExportStore.show(this.exportId)
    },
    downloadXml() {
      return this.sepaDirectDebitExportStore.downloadXml(this.exportId)
    },
    submitExport() {
      if (!window.confirm(this.$t('sepa_direct_debits.submit_confirm'))) {
        return
      }
      this.sepaDirectDebitExportStore.submit(this.exportId).then(() => {
        Helpers.emitter.emit('show-toast', {severity: 'success', summary: this.$t('sepa_direct_debits.submit_success')})
        this.loadExport()
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('sepa_direct_debits.submit_failed')
        })
      })
    },
    discardExport() {
      if (!window.confirm(this.$t('sepa_direct_debits.discard_confirm'))) {
        return
      }
      this.sepaDirectDebitExportStore.discard(this.exportId).then(() => {
        Helpers.emitter.emit('show-toast', {severity: 'success', summary: this.$t('sepa_direct_debits.discard_success')})
        this.loadExport()
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('sepa_direct_debits.discard_failed')
        })
      })
    },
  },
}
</script>
